import { ApolloError, gql, useLazyQuery } from '@apollo/client';
import {
  EqlQueryPropertiesViewFragmentDoc,
  GenerateEqlRequest,
  Query,
} from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';
import { useNotificationContext } from '../components/Notification/NotificationContext';
import { NOTIFICATION_TIMEOUT_LONG_8S } from '../constants';

const GENERATE_EQL = gql`
  query generateEql(
    $eqlType: EQLType!
    $query: String!
    $tenantID: String!
    $userID: String!
  ) {
    generateEql(
      request: {
        eqlType: $eqlType
        query: $query
        tenantID: $tenantID
        userID: $userID
      }
    ) {
      eql {
        eql
        properties {
          ...EqlQueryPropertiesView
        }
      }
      errorMessage
    }
  }
  ${EqlQueryPropertiesViewFragmentDoc}
`;

export interface useGenerateEqlProps {
  onCompleted?: () => void;
  onError?: () => void;
}

export function useGenerateEql({
  onCompleted,
  onError,
}: useGenerateEqlProps = {}) {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();
  const { showErrorNotification } = useNotificationContext();

  const [generateEqlQuery, { data, loading }] = useLazyQuery<
    Query,
    GenerateEqlRequest
  >(GENERATE_EQL, {
    fetchPolicy: 'network-only',
    onError: (error: ApolloError) => {
      if (error) {
        showErrorNotification({
          contentComponent: error.message || 'Failed to generate filters from given prompt.',
          props: {
            timeout: NOTIFICATION_TIMEOUT_LONG_8S,
          },
        });
      }

      onError?.();
    },
    onCompleted: (data: Query) => {
      if (data.generateEql?.errorMessage) {
        showErrorNotification({
          contentComponent: data.generateEql.errorMessage,
          props: {
            timeout: NOTIFICATION_TIMEOUT_LONG_8S,
          },
        });
      }

      onCompleted?.();
    },
  });

  const generateEql = useCallback(
    async (input: Omit<GenerateEqlRequest, 'tenantID' | 'userID'>) => {
      const resp = await generateEqlQuery({
        variables: {
          tenantID,
          userID,
          ...input,
        },
      });

      return resp.data?.generateEql?.eql;
    },
    [generateEqlQuery, tenantID, userID],
  );

  return [
    generateEql,
    {
      data: data?.generateEql?.eql,
      isLoading: loading,
    },
  ] as const;
}
